import { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import SelectComponent from "components/common/select";
import {
  CaretDown,
  CaretRight,
  DownloadSimple,
  Plus,
  Trash,
} from "phosphor-react";
import "./housingDatabaseModal.scss";
import Check from "components/common/check";
import ToggleButton from "components/common/togglebutton";
import Input from "components/common/input";
import Radio from "components/common/radio";
import AsyncSelect from "components/common/select/AsyncSelect";
import {
  useDebouncedCitiesDataCallback,
  useDebouncedCountiesDataCallback,
  useDebouncedStatesDataCallback,
  useDebouncedZipCodesDataCallback,
} from "utils/debouncedHelper";
import {
  getCities,
  getCounties,
  getStates,
  getZipCodes,
} from "containers/policies/api";
import { initialMetaForTable, normalizeInput } from "constants/common";
import { capitalizeName, formatLocation, sortData } from "utils/common";

// Rent and Income range options
const rentRanges = [
  { value: "0-1749", label: "$0 - $1,749" },
  { value: "1750-1949", label: "$1,750 - $1,949" },
  { value: "1950-2349", label: "$1,950 - $2,349" },
  { value: "2350-3149", label: "$2,350 - $3,149" },
  { value: "3150-3675", label: "$3,150 - $3,675" },
  { value: "3676-4000", label: "$3,676 - $4,000" },
];

const incomeRanges = [
  { value: "0-30000", label: "$0 - $30,000" },
  { value: "30001-58020", label: "$30,001 - $58,020" },
  { value: "58021-94000", label: "$58,021 - $94,000" },
  { value: "94001-153000", label: "$94,001 - $153,000" },
];

export const applicationFeeOptions = [
  { value: "0-20", label: "0 - 20" },
  { value: "21-50", label: "21 - 50" },
  { value: "51-75", label: "51 - 75" },
  { value: "76-100", label: "76 - 100" },
];

export const ageRequirementOptions = [
  { value: "0-20", label: "0 - 20" },
  { value: "21-35", label: "21 - 35" },
  { value: "36-45", label: "36 - 45" },
  { value: "46-55", label: "46 - 55" },
  { value: "56-65", label: "56 - 65" },
  { value: "66-75", label: "66 - 75" },
  { value: "76-85", label: "76 - 85" },
  { value: "86-100", label: "86 - 100" },
];

export const securityDepositOptions = [
  { value: "0-1749", label: "0 - 1,749" },
  { value: "1750-1949", label: "1,750 - 1,949" },
  { value: "1950-2349", label: "1,950 - 2,349" },
  { value: "2350-3149", label: "2,350 - 3,149" },
  { value: "3150-3675", label: "3,150 - 3,675" },
  { value: "3676-4000", label: "3,676 - 4,000" },
  { value: "4001-5000", label: "4,001 - 5,000" },
];

export const creditScoreOptions = [
  { value: "300-579", label: "300 - 579" },
  { value: "580-669", label: "580 - 669" },
  { value: "670-739", label: "670 - 739" },
  { value: "740-799", label: "740 - 799" },
  { value: "800-850", label: "800 - 850" },
];
const FILE_SIZE_LIMIT = 2 * 1024 * 1024;

// Yup validation schema
const validationSchema = Yup.object().shape({
  status: Yup.string()
    .oneOf(["active", "inactive"])
    .required("Status is required"),
  property_name: Yup.string().required("Property Name is required"),
  email: Yup.string()
    .email("Invalid email")
    .required("Email is required"),
  street: Yup.string().required("Address is required"),
  state: Yup.mixed().required("State is required"),
  county: Yup.mixed().required("County is required"),
  city: Yup.mixed().required("City is required"),
  zip_code: Yup.mixed().required("ZIP Code is required"),
  key_contacts: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().nullable(),
      name: Yup.string(),
      designation: Yup.string(),
      key_contact_email: Yup.string().email("Invalid email"),
      phone: Yup.string().nullable(),
      primary: Yup.boolean(),
      _destroy: Yup.boolean(),
    }),
  ),
  units: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().nullable(),
      type_of_unit: Yup.string(),
      number_of_units: Yup.number()
        .positive("Must be a positive number")
        .integer("Must be an integer"),
      available_units: Yup.number()
        .positive("Must be a positive number")
        .integer("Must be an integer"),
      primary: Yup.boolean(),
      _destroy: Yup.boolean(),
    }),
  ),
  resident_selection: Yup.object().shape({
    income_range: Yup.string(),
  }),
  criteria: Yup.object().shape({
    no_eviction: Yup.boolean().nullable(),
    credit_check: Yup.boolean().nullable(),
    background: Yup.boolean().nullable(),
    rental_history: Yup.boolean().nullable(),
    application_fee: Yup.boolean().nullable(),
    application_fee_amount: Yup.string().when("application_fee", {
      is: true,
      then: Yup.string().required("Application Fee amount is required"),
      otherwise: Yup.string().nullable(),
    }),
    age_requirement: Yup.boolean().nullable(),
    age_requirement_value: Yup.string().when("age_requirement", {
      is: true,
      then: Yup.string().required("Age requirement is required"),
      otherwise: Yup.string().nullable(),
    }),
    security_deposit: Yup.boolean().nullable(),

    security_deposit_amount: Yup.string().when("security_deposit", {
      is: true,
      then: Yup.string().required("Security deposit amount is required"),
      otherwise: Yup.string().nullable(),
    }),
    no_bankruptcy: Yup.boolean().nullable(),
    willing_to_take_eviction: Yup.boolean().nullable(),

    no_3rd_party_checks: Yup.boolean().nullable(),
    credit_score: Yup.boolean().nullable(),

    credit_score_range: Yup.string().when("credit_score", {
      is: true,
      then: Yup.string().required("Credit score range is required"),
      otherwise: Yup.string().nullable(),
    }),
  }),
  comment: Yup.string(),
  online_application: Yup.string()
    .url("Must be a valid URL")
    .nullable(),
  file_upload: Yup.mixed().when("_action", {
    is: "complete",
    then: (schema) =>
      schema
        .required("Document is Required")
        .test(
          "fileSize",
          "File size must be less than or equal to 2 MB",
          (value) => {
            if (value?.url) return true;
            return (
              !value || (value?.file && value.file.size <= FILE_SIZE_LIMIT)
            );
          },
        ),
    otherwise: (schema) =>
      schema
        .notRequired()
        .test(
          "fileSize",
          "File size must be less than or equal to 2 MB",
          (value) => {
            if (value?.url) return true;
            return (
              !value || (value?.file && value.file.size <= FILE_SIZE_LIMIT)
            );
          },
        ),
  }),
});

const findClosestRange = (from, to, ranges) => {
  if (!from || !to || !ranges || ranges.length === 0) return "";

  const fromNum = Number(from);
  const toNum = Number(to);
  if (isNaN(fromNum) || isNaN(toNum)) return "";
  for (const range of ranges) {
    const [rangeFrom, rangeTo] = range.value.split("-").map(Number);
    if (fromNum >= rangeFrom && toNum <= rangeTo) {
      return range.value;
    }
  }
  let closestRange = ranges[0].value;
  let minDiff = Infinity;

  for (const range of ranges) {
    const [rangeFrom] = range.value.split("-").map(Number);
    const diff = Math.abs(fromNum - rangeFrom);
    if (diff < minDiff) {
      minDiff = diff;
      closestRange = range.value;
    }
  }

  return closestRange;
};

const HousingDatabaseModal = ({
  handleQuestionSubmittion,
  handleClose,
  selectedMember,
}) => {
  const [expandedRows, setExpandedRows] = useState({
    contacts: {},
    units: {},
  });
  const unitTypes = ["SRO", "Studio", "1BR", "2BR", "3BR", "4BR"];

  const [statesList, setStatesList] = useState([]);
  const [countiesList, setCountiesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [zipCodesList, setZipCodesList] = useState([]);

  const debouncedStatesDataCallback = useDebouncedStatesDataCallback();
  const debouncedCountiesDataCallback = useDebouncedCountiesDataCallback();
  const debouncedCitiesDataCallback = useDebouncedCitiesDataCallback();
  const debouncedZipCodesDataCallback = useDebouncedZipCodesDataCallback();

  const fetchStates = async () => {
    const result = await getStates({ ...initialMetaForTable });
    if (result["states"]) {
      const data = result?.states;
      setStatesList(sortData(data));
    }
  };

  const fetchCounties = async (stateId) => {
    const result = await getCounties({
      ...initialMetaForTable,
      state_id: stateId,
    });
    if (result["counties"]) {
      setCountiesList(sortData(result.counties));
    }
  };

  const fetchCities = async (stateId, countyId) => {
    const result = await getCities({
      ...initialMetaForTable,
      state_id: stateId,
      county_id: countyId,
    });
    if (result["cities"]) {
      setCitiesList(sortData(result.cities));
    }
  };

  const fetchZipCodes = async (stateId, countyId, cityId) => {
    const result = await getZipCodes({
      ...initialMetaForTable,
      state_id: stateId,
      county_id: countyId,
      city_id: cityId,
    });
    if (result["zip_codes"]) {
      setZipCodesList(result.zip_codes);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (selectedMember?.addresses?.[0]?.state_id) {
      fetchCounties(selectedMember.addresses[0].state_id);
    }
  }, [selectedMember]);

  useEffect(() => {
    if (
      selectedMember?.addresses?.[0]?.state_id &&
      selectedMember?.addresses?.[0]?.county_id
    ) {
      fetchCities(
        selectedMember.addresses[0].state_id,
        selectedMember.addresses[0].county_id,
      );
    }
  }, [selectedMember]);

  useEffect(() => {
    if (
      selectedMember?.addresses?.[0]?.state_id &&
      selectedMember?.addresses?.[0]?.county_id &&
      selectedMember?.addresses?.[0]?.city_id
    ) {
      fetchZipCodes(
        selectedMember.addresses[0].state_id,
        selectedMember.addresses[0].county_id,
        selectedMember.addresses[0].city_id,
      );
    }
  }, [selectedMember]);

  const toggleRow = (type, index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [type]: { ...prev[type], [index]: !prev[type][index] },
    }));
  };

  const transformValues = (values) => {
    const zipCodeId =
      zipCodesList.find((z) => z.name === values.zip_code)?.id || null;
    const transformed = {
      housing_database: {
        property_name: values.property_name,
        email: values.email,
        income_from: values.resident_selection.income_range
          ? values.resident_selection.income_range.split("-")[0] || null
          : null,
        income_to: values.resident_selection.income_range
          ? values.resident_selection.income_range.split("-")[1] || null
          : null,
        rent_from: values.resident_selection.rent_range
          ? values.resident_selection.rent_range.split("-")[0] || null
          : null,
        rent_to: values.resident_selection.rent_range
          ? values.resident_selection.rent_range.split("-")[1] || null
          : null,
        no_eviction: values.criteria.no_eviction,
        credit_check: values.criteria.credit_check,
        background: values.criteria.background,
        rental_history: values.criteria.rental_history,
        application_fee: values.criteria.application_fee,
        application_fee_amount: values.criteria.application_fee_amount,
        age_requirement: values.criteria.age_requirement,
        age_requirement_value: values.criteria.age_requirement_value,
        security_deposit: values.criteria.security_deposit,
        security_deposit_amount: values.criteria.security_deposit_amount,
        no_bankruptcy: values.criteria.no_bankruptcy,
        willing_to_take_eviction: values.criteria.willing_to_take_eviction,
        no_3rd_party_checks: values.criteria.no_3rd_party_checks,
        credit_score: values.criteria.credit_score,
        credit_score_range: values.criteria.credit_score_range,
        online_application: values.online_application,
        comment: values.comment,
        status: values.status,
        key_contacts_attributes: values.key_contacts.map((contact) => ({
          id: contact.id || null,
          name: contact.name,
          designation: contact.designation,
          email: contact.email,
          phone: contact.phone,
          primary: contact.primary || false,
          _destroy: contact._destroy || false,
        })),
        units_attributes: values.units.map((unit) => ({
          id: unit.id || null,
          type_of_unit: unit.type_of_unit,
          number_of_units: unit.number_of_units,
          available_units: unit.available_units,
          primary: unit.primary || false,
          _destroy: unit._destroy || false,
        })),
        addresses_attributes: [
          {
            ...(selectedMember?.addresses?.[0]?.id && {
              id: selectedMember.addresses[0].id,
            }),
            street: values.street || "",
            city: values.city?.label || "",
            state: values.state?.label || "",
            county: values.county?.label || "",
            zip_code: values.zip_code?.label || "",
            city_id: values.city?.value || "",
            state_id: values.state?.value || "",
            county_id: values.county?.value || "",
            zip_code_id: values.zip_code?.value || null,
          },
        ],
        file_upload: null,
        content_type: "",
        file_name: "",
      },
    };
    return transformed;
  };

  const initialKeyContacts =
    selectedMember?.key_contacts && selectedMember.key_contacts.length > 0
      ? selectedMember.key_contacts.map((contact) => ({
          id: contact.id || null,
          name: contact.name || "",
          designation: contact.designation || "",
          email: contact.email || "",
          phone: contact.phone || "",
          primary: contact.primary || false,
          _destroy: false,
        }))
      : [];

  const initialUnits =
    selectedMember?.units && selectedMember.units.length > 0
      ? selectedMember.units.map((unit) => ({
          id: unit.id || null,
          type_of_unit: unit.type_of_unit || "",
          number_of_units: unit.number_of_units || "",
          available_units: unit.available_units || "",
          primary: unit.primary || false,
          _destroy: false,
        }))
      : [];

  const initialIncomeRange = selectedMember
    ? findClosestRange(
        selectedMember.income_from,
        selectedMember.income_to,
        incomeRanges,
      )
    : "";

  const initialRentRange = selectedMember
    ? findClosestRange(
        selectedMember.rent_from,
        selectedMember.rent_to,
        rentRanges,
      )
    : "";

  const AddressOptions = (options) => {
    return options.filter(
      (v, i, a) => a.findIndex((t) => t.value === v.value) === i,
    );
  };
  const fileInputRef = useRef(null);

  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  const renderUploadBox = (name, label, setFieldValue, isRequired, values) => {
    const fileValue = values?.[name];
    return (
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">{label}</label>
        <div className="border border-warning rounded p-4 text-center bg-light h-100">
          {!fileValue ? (
            <>
              <div className="mb-2">
                <i className="bi bi-upload" style={{ fontSize: "24px" }}></i>
              </div>

              <span>Drag & drop or </span>
              <label
                className="text-warning fw-bold"
                style={{ cursor: "pointer" }}
              >
                choose file
                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setFieldValue(name, {
                      file: e.target.files[0],
                      url: null,
                      name: e.target.files[0]?.name,
                    })
                  }
                />
              </label>
              <div className="text-muted small mt-1">(Max. 2 MB)</div>
            </>
          ) : (
            <>
              <span>Drag & drop or </span>
              <label
                className="text-warning fw-bold"
                style={{ cursor: "pointer" }}
              >
                choose file
                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setFieldValue(name, {
                      file: e.target.files[0],
                      url: null,
                      name: e.target.files[0]?.name,
                    })
                  }
                />
              </label>
              <div className="text-muted small mt-1">(Max. 2 MB )</div>

              <Trash
                size={20}
                className="cursor-pointer opacity-50"
                onClick={() => setFieldValue(name, null)}
              ></Trash>
            </>
          )}
        </div>
      </div>
    );
  };
  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        status: selectedMember?.status || "active",
        property_name: selectedMember?.property_name || "",
        email: selectedMember?.email || "",
        street: selectedMember?.addresses?.[0]?.street || "",
        state: selectedMember?.addresses?.[0]?.state
          ? {
              value: selectedMember.addresses[0].state.id,
              label: selectedMember.addresses[0].state.name.toUpperCase(),
            }
          : "",
        county: selectedMember?.addresses?.[0]?.county
          ? {
              value: selectedMember.addresses[0].county.id,
              label: formatLocation(selectedMember.addresses[0].county.name),
            }
          : "",
        city: selectedMember?.addresses?.[0]?.city
          ? {
              value: selectedMember.addresses[0].city.id,
              label: capitalizeName(selectedMember.addresses[0].city.name),
            }
          : "",
        zip_code: selectedMember?.addresses?.[0]?.zip_code
          ? {
              value: selectedMember.addresses[0].zip_code.id,
              label: selectedMember.addresses[0].zip_code.name,
            }
          : "",
        key_contacts: initialKeyContacts,
        units: initialUnits,
        resident_selection: {
          income_range: initialIncomeRange,
          rent_range: initialRentRange,
        },
        criteria: {
          no_eviction: selectedMember?.no_eviction ?? null,
          credit_check: selectedMember?.credit_check ?? null,
          background: selectedMember?.background ?? null,
          rental_history: selectedMember?.rental_history ?? null,
          application_fee: selectedMember?.application_fee ?? null,
          application_fee_amount: selectedMember?.application_fee_amount || "",
          age_requirement: selectedMember?.age_requirement ?? null,
          age_requirement_value: selectedMember?.age_requirement_value || "",
          security_deposit: selectedMember?.security_deposit ?? null,
          security_deposit_amount:
            selectedMember?.security_deposit_amount || "",
          no_bankruptcy: selectedMember?.no_bankruptcy ?? null,
          willing_to_take_eviction:
            selectedMember?.willing_to_take_eviction ?? null,
          no_3rd_party_checks: selectedMember?.no_3rd_party_checks ?? null,
          credit_score: selectedMember?.credit_score ?? null,
          credit_score_range: selectedMember?.credit_score_range || "",
        },
        comment: selectedMember?.comment || "",
        online_application: selectedMember?.online_application || "",
        content_type: selectedMember?.content_type,

        file_upload: selectedMember?.file_upload
          ? {
              file: null,
              url: selectedMember.file_upload,
              name: selectedMember.file_name,
            }
          : null,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        try {
          setSubmitting(true);
          const transformedData = transformValues(values);
          const payload = {
            ...transformedData,
            file_upload: values.file_upload?.file
              ? await convertFileToBase64(values.file_upload.file)
              : values.file_upload?.url || null,
            file_name:
              values.file_upload?.file?.name ||
              values.file_upload?.name ||
              null,
          };
          await handleQuestionSubmittion(payload, setErrors);
        } catch (error) {
          console.error(error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Form className="row" style={{ background: "white" }}>
          {/* Status */}
          <div className="col-sm-12">
            <Field name="status">
              {({ field }) => (
                <ToggleButton
                  label="Status"
                  name={field.name}
                  value={field.value === "active"}
                  handleChange={(name, value) =>
                    setFieldValue(name, value ? "active" : "inactive")
                  }
                />
              )}
            </Field>
          </div>

          {/* Property Name */}
          <div className="col-sm-6">
            <Field name="property_name">
              {({ field }) => (
                <Input
                  {...field}
                  isRequired
                  placeholder="e.g. John Doe"
                  type="text"
                  label="Property Name"
                />
              )}
            </Field>
            <ErrorMessage
              name="property_name"
              component="div"
              className="text-danger"
            />
          </div>

          {/* Email */}
          <div className="col-sm-6">
            <Field name="email">
              {({ field }) => (
                <Input
                  isRequired
                  {...field}
                  placeholder="e.g. johndoe@gmail.com"
                  type="email"
                  label="Email"
                />
              )}
            </Field>
            <ErrorMessage
              name="email"
              component="div"
              className="text-danger"
            />
          </div>

          {/* Street */}
          <div className="col-sm-6">
            <Field name="street">
              {({ field }) => (
                <Input
                  {...field}
                  isRequired
                  placeholder="e.g. 1234 Main St."
                  type="text"
                  label="Address"
                />
              )}
            </Field>
            <ErrorMessage
              name="street"
              component="div"
              className="text-danger"
            />
          </div>

          {/* State */}
          <div className="col-sm-3">
            <AsyncSelect
              label="State"
              isRequired
              options={AddressOptions([
                ...(statesList?.map((el) => ({
                  value: el.id,
                  label: el.name.toUpperCase(),
                })) || []),
                ...(selectedMember?.addresses?.[0]?.state
                  ? [
                      {
                        value: selectedMember.addresses[0].state.id,
                        label: selectedMember.addresses[0].state.name.toUpperCase(),
                      },
                    ]
                  : []),
              ])}
              loadOptions={(inputValue) =>
                new Promise((resolve) =>
                  debouncedStatesDataCallback(inputValue, resolve),
                )
              }
              selectedValue={values.state}
              placeholder="Select State"
              handleChange={(obj) => {
                setFieldValue("state", obj);
                setFieldValue("county", "");
                setFieldValue("city", "");
                setFieldValue("zip_code", "");
                fetchCounties(obj.value);
              }}
              error={
                <ErrorMessage
                  className="error-message"
                  component="p"
                  name="state"
                />
              }
            />
          </div>

          {/* County */}
          <div className="col-sm-3">
            <AsyncSelect
              label="County"
              isRequired
              disabled={!values.state?.value}
              options={AddressOptions([
                ...(countiesList?.map((el) => ({
                  value: el.id,
                  label: formatLocation(el.name),
                })) || []),
                ...(selectedMember?.addresses?.[0]?.county
                  ? [
                      {
                        value: selectedMember.addresses[0].county.id,
                        label: formatLocation(
                          selectedMember.addresses[0].county.name,
                        ),
                      },
                    ]
                  : []),
              ])}
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedCountiesDataCallback(
                    inputValue,
                    values.state?.value,
                    resolve,
                  );
                })
              }
              selectedValue={values.county}
              placeholder="Select County"
              handleChange={(obj) => {
                setFieldValue("county", obj);
                setFieldValue("city", "");
                setFieldValue("zip_code", "");
                fetchCities(values.state?.value, obj.value);
              }}
              error={
                <ErrorMessage
                  className="error-message"
                  component="p"
                  name="county"
                />
              }
            />
          </div>

          {/* City */}
          <div className="col-sm-3">
            <AsyncSelect
              label="City"
              isRequired
              disabled={!values.county?.value}
              options={AddressOptions([
                ...(citiesList?.map((el) => ({
                  value: el.id,
                  label: capitalizeName(el.name),
                })) || []),
                ...(selectedMember?.addresses?.[0]?.city
                  ? [
                      {
                        value: selectedMember.addresses[0].city.id,
                        label: capitalizeName(
                          selectedMember.addresses[0].city.name,
                        ),
                      },
                    ]
                  : []),
              ])}
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedCitiesDataCallback(
                    inputValue,
                    values.state?.value,
                    values.county?.value,
                    resolve,
                  );
                })
              }
              selectedValue={values.city}
              placeholder="Select City"
              handleChange={(obj) => {
                setFieldValue("city", obj);
                setFieldValue("zip_code", "");
                fetchZipCodes(
                  values.state?.value,
                  values.county?.value,
                  obj.value,
                );
              }}
              error={
                <ErrorMessage
                  className="error-message"
                  component="p"
                  name="city"
                />
              }
            />
          </div>

          <div className="col-sm-3">
            <AsyncSelect
              name="zip_code"
              isRequired
              label="ZIP Code"
              disabled={!values.city?.value}
              options={
                zipCodesList?.map((el) => ({
                  value: el.id,
                  label: el.name,
                })) || []
              }
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedZipCodesDataCallback(
                    inputValue,
                    values.state?.value,
                    values.county?.value,
                    values.city?.value,
                    resolve,
                  );
                })
              }
              selectedValue={values.zip_code}
              handleChange={(obj) => setFieldValue("zip_code", obj)}
              placeholder="Select ZIP Code"
              className="mb-1"
              isClearable={true}
              error={
                <ErrorMessage
                  className="error-message"
                  component="p"
                  name="zip_code"
                />
              }
            />
          </div>

          {/* Key Contacts */}
          <div className="col-sm-12">
            <div className="fs_20 py-2">Key Contacts</div>
            <FieldArray name="key_contacts">
              {({ push, remove }) => (
                <div className="background p-3">
                  {values.key_contacts.map((contact, index) => {
                    if (contact._destroy) return null;
                    return (
                      <div key={contact.id || `contact-${index}`}>
                        <div className="d-flex w-100 justify-content-between">
                          <div className="d-flex w-100 gap-2 align-items-center">
                            {expandedRows.contacts[index] ? (
                              <CaretDown
                                size={20}
                                className="cursor-pointer"
                                onClick={() => toggleRow("contacts", index)}
                              />
                            ) : (
                              <CaretRight
                                size={20}
                                className="cursor-pointer opacity-50"
                                onClick={() => toggleRow("contacts", index)}
                              />
                            )}
                            <div
                              className={`heading-smb ${
                                !expandedRows.contacts[index]
                                  ? "opacity-75"
                                  : "opacity-100"
                              }`}
                            >
                              Contact {index + 1}
                            </div>
                          </div>
                          <div className="d-flex w-100 gap-3 align-items-center justify-content-end">
                            <Field
                              type="checkbox"
                              name={`key_contacts[${index}].primary`}
                            >
                              {({ field }) => (
                                <Radio
                                  name={field.name}
                                  value={field.value}
                                  checked={field.value}
                                  label="Primary"
                                  handleChange={() =>
                                    setFieldValue(field.name, !field.value)
                                  }
                                />
                              )}
                            </Field>
                            <Trash
                              size={20}
                              className="cursor-pointer opacity-50"
                              onClick={() => {
                                if (contact?.id) {
                                  setFieldValue(
                                    `key_contacts[${index}]._destroy`,
                                    true,
                                  );
                                } else {
                                  remove(index);
                                }
                              }}
                            />
                          </div>
                        </div>

                        {expandedRows.contacts[index] && (
                          <div className="row">
                            <Field
                              name={`key_contacts[${index}].id`}
                              type="hidden"
                            />
                            <Field
                              name={`key_contacts[${index}]._destroy`}
                              type="hidden"
                            />
                            <div className="col-sm-6">
                              <Field name={`key_contacts[${index}].name`}>
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="e.g. John Doe"
                                    type="text"
                                    label="Contact Name"
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name={`key_contacts[${index}].name`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                            <div className="col-sm-6">
                              <Field
                                name={`key_contacts[${index}].designation`}
                              >
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="e.g. Program Coordinator"
                                    type="text"
                                    label="Designation"
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name={`key_contacts[${index}].designation`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                            <div className="col-sm-6">
                              <Field name={`key_contacts[${index}].email`}>
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="e.g. johndoe@gmail.com"
                                    type="email"
                                    label="Email"
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name={`key_contacts[${index}].email`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                            <div className="col-sm-6">
                              <Field name={`key_contacts[${index}].phone`}>
                                {({ field, form }) => (
                                  <Input
                                    {...field}
                                    placeholder="e.g. (555) 123-4567"
                                    type="text"
                                    label="Phone"
                                    onChange={(e) => {
                                      const formattedNumber = normalizeInput(
                                        e.target.value,
                                      );
                                      form.setFieldValue(
                                        `key_contacts[${index}].phone`,
                                        formattedNumber,
                                      );
                                    }}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name={`key_contacts[${index}].phone`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    className="secondary-btn w-100 mt-4"
                    type="button"
                    onClick={() =>
                      push({
                        id: null,
                        name: "",
                        designation: "",
                        email: "",
                        phone: "",
                        primary: false,
                        _destroy: false,
                      })
                    }
                  >
                    <Plus size={18} /> Add Contact
                  </button>
                </div>
              )}
            </FieldArray>
          </div>

          {/* Units */}
          <div className="col-sm-12 p-3">
            <div className="fs_20 py-2">Units</div>
            <FieldArray name="units">
              {({ push, remove }) => (
                <div className="background p-3">
                  {values.units.map((unit, index) => {
                    if (unit._destroy) return null;
                    return (
                      <div key={unit.id || `unit-${index}`}>
                        <div className="d-flex w-100 justify-content-between">
                          <div className="d-flex w-100 gap-2 align-items-center">
                            {expandedRows.units[index] ? (
                              <CaretDown
                                size={20}
                                className="cursor-pointer"
                                onClick={() => toggleRow("units", index)}
                              />
                            ) : (
                              <CaretRight
                                size={20}
                                className="cursor-pointer opacity-50"
                                onClick={() => toggleRow("units", index)}
                              />
                            )}
                            <div
                              className={`heading-smb ${
                                !expandedRows.units[index]
                                  ? "opacity-75"
                                  : "opacity-100"
                              }`}
                            >
                              Unit {index + 1}
                            </div>
                          </div>
                          <div className="d-flex w-100 gap-3 align-items-center justify-content-end">
                            <Trash
                              size={20}
                              className="cursor-pointer opacity-50"
                              onClick={() => {
                                if (unit.id) {
                                  setFieldValue(
                                    `units[${index}]._destroy`,
                                    true,
                                  );
                                } else {
                                  remove(index);
                                }
                              }}
                            />
                          </div>
                        </div>

                        {expandedRows.units[index] && (
                          <div className="row w-100">
                            <Field name={`units[${index}].id`} type="hidden" />
                            <Field
                              name={`units[${index}]._destroy`}
                              type="hidden"
                            />
                            <div className="col-sm-4">
                              <Field name={`units[${index}].type_of_unit`}>
                                {({ field }) => (
                                  <SelectComponent
                                    name={field.name}
                                    label="Type of Units"
                                    className="mb-1 bg-transparent"
                                    options={unitTypes.map((type) => ({
                                      value: type.toLowerCase(),
                                      label: type,
                                    }))}
                                    handleChange={(obj) =>
                                      setFieldValue(field.name, obj.value)
                                    }
                                    placeholder="Select"
                                    value={unitTypes
                                      .map((type) => ({
                                        value: type.toLowerCase(),
                                        label: type,
                                      }))
                                      .find((opt) => opt.value === field.value)}
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name={`units[${index}].type_of_unit`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                            <div className="col-sm-4">
                              <Field name={`units[${index}].number_of_units`}>
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="e.g. 5"
                                    type="number"
                                    label="No. of Units"
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name={`units[${index}].number_of_units`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                            <div className="col-sm-4">
                              <Field name={`units[${index}].available_units`}>
                                {({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="e.g. 7"
                                    type="number"
                                    label="Available Units"
                                  />
                                )}
                              </Field>
                              <ErrorMessage
                                name={`units[${index}].available_units`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    className="secondary-btn w-100 mt-4"
                    type="button"
                    onClick={() =>
                      push({
                        id: null,
                        type_of_unit: "",
                        number_of_units: "",
                        available_units: "",
                        primary: false,
                        _destroy: false,
                      })
                    }
                  >
                    <Plus size={18} /> Add Unit
                  </button>
                </div>
              )}
            </FieldArray>
          </div>

          {/* Resident Selection Criteria */}
          <div className="col-sm-12">
            <div className="fs_20 py-2">Resident Selection Criteria</div>
            <div className="row">
              <div className="col-sm-6">
                <Field name="resident_selection.income_range">
                  {({ field }) => {
                    const selectedOption = field.value
                      ? incomeRanges.find((opt) => opt.value === field.value)
                      : null;
                    return (
                      <SelectComponent
                        name={field.name}
                        label="Income Range"
                        options={incomeRanges}
                        handleChange={(obj) =>
                          setFieldValue(field.name, obj ? obj.value : "")
                        }
                        placeholder="Select Income Range"
                        value={selectedOption}
                        isClearable={true}
                      />
                    );
                  }}
                </Field>
                <ErrorMessage
                  name="resident_selection.income_range"
                  component="div"
                  className="text-danger"
                />
              </div>
              <div className="col-sm-6">
                <Field name="resident_selection.rent_range">
                  {({ field }) => {
                    const selectedOption = field.value
                      ? rentRanges.find((opt) => opt.value === field.value)
                      : null;
                    return (
                      <SelectComponent
                        name={field.name}
                        label="Rent Range"
                        options={rentRanges}
                        handleChange={(obj) =>
                          setFieldValue(field.name, obj ? obj.value : "")
                        }
                        placeholder="Select Rent Range"
                        value={selectedOption}
                        isClearable={true}
                      />
                    );
                  }}
                </Field>
                <ErrorMessage
                  name="resident_selection.rent_range"
                  component="div"
                  className="text-danger"
                />
              </div>
            </div>
          </div>

          {/* Yes/No Dropdowns with Conditional Fields */}
          <div className="col-sm-12 p-3">
            <div className="row">
              {[
                { label: "No Eviction", name: "no_eviction" },
                { label: "Credit Check", name: "credit_check" },
                { label: "Background", name: "background" },
                { label: "Rental History", name: "rental_history" },
                {
                  label: "Application Fee",
                  name: "application_fee",
                  dropdown: {
                    name: "application_fee_amount",
                    options: applicationFeeOptions,
                    label: "Application Fee Amount",
                    placeholder: "Select Fee Amount",
                  },
                },
                {
                  label: "Age Requirement",
                  name: "age_requirement",
                  dropdown: {
                    name: "age_requirement_value",
                    options: ageRequirementOptions,
                    label: "Age Requirement",
                    placeholder: "Select Age Requirement",
                  },
                },
                {
                  label: "Security Deposit",
                  name: "security_deposit",
                  dropdown: {
                    name: "security_deposit_amount",
                    options: securityDepositOptions,
                    label: "Security Deposit Amount",
                    placeholder: "Select Deposit Amount",
                  },
                },
                { label: "No Bankruptcy", name: "no_bankruptcy" },
                // {
                //   label: "Willing to take Evictions",
                //   name: "willing_to_take_eviction",
                // },
                { label: "No 3rd Party Checks", name: "no_3rd_party_checks" },
                {
                  label: "Credit Score",
                  name: "credit_score",
                  dropdown: {
                    name: "credit_score_range",
                    options: creditScoreOptions,
                    label: "Credit Score Range",
                    placeholder: "Select Credit Score Range",
                  },
                },
              ].map((item) => (
                <div className="col-sm-4 mb-3" key={item.name}>
                  <Field name={`criteria.${item.name}`}>
                    {({ field, form }) => {
                      const yesNoOptions = [
                        { value: "true", label: "Yes" },
                        { value: "false", label: "No" },
                      ];
                      const selectedOption =
                        field.value !== undefined
                          ? yesNoOptions.find(
                              (opt) => opt.value === String(field.value),
                            )
                          : null;

                      return (
                        <>
                          <SelectComponent
                            name={field.name}
                            label={item.label}
                            options={yesNoOptions}
                            value={selectedOption}
                            handleChange={(obj) => {
                              const value = obj ? obj.value === "true" : null;
                              form.setFieldValue(field.name, value);

                              if (item.dropdown && value === false) {
                                form.setFieldValue(
                                  `criteria.${item.dropdown.name}`,
                                  "",
                                );
                              }
                            }}
                            placeholder="Select"
                            isClearable={false}
                            isRequired={item.required}
                          />
                          {item.required && (
                            <ErrorMessage
                              name={field.name}
                              component="div"
                              className="text-danger"
                            />
                          )}
                        </>
                      );
                    }}
                  </Field>

                  {item.dropdown && values.criteria[item.name] === true && (
                    <div className="mt-2">
                      <Field name={`criteria.${item.dropdown.name}`}>
                        {({ field }) => (
                          <SelectComponent
                            name={field.name}
                            label={item.dropdown.label}
                            isRequired={item.required}
                            options={item.dropdown.options}
                            handleChange={(obj) =>
                              setFieldValue(field.name, obj ? obj.value : "")
                            }
                            placeholder={item.dropdown.placeholder}
                            value={
                              item.dropdown.options.find(
                                (opt) => opt.value === field.value,
                              ) || null
                            }
                            isClearable={true}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name={`criteria.${item.dropdown.name}`}
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Additional Comments */}
          <div className="col-sm-12 p-3">
            <Field name="comment">
              {({ field }) => (
                <Input
                  {...field}
                  placeholder="Write any additional notes"
                  type="text"
                  label="Additional Comments"
                />
              )}
            </Field>
            <ErrorMessage
              name="comment"
              component="div"
              className="text-danger"
            />
          </div>

          {/* Online Application */}
          <div className="col-sm-12 p-3">
            <Field name="online_application">
              {({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. https://example.com/application"
                  type="url"
                  label="Online Application"
                />
              )}
            </Field>
            <ErrorMessage
              name="online_application"
              component="div"
              className="text-danger"
            />
          </div>

          {/* File Upload */}
          <div className="col-sm-12">
            {renderUploadBox(
              "file_upload",
              "Upload File",
              setFieldValue,
              true,
              values,
            )}
            <ErrorMessage
              name="file_upload"
              component="p"
              style={{ color: "red", fontSize: "0.8rem" }}
            />
            {values?.file_upload && (
              <p className="text-sm text-gray-600 mt-2">
                Selected: {values.file_upload.name}
              </p>
            )}
            {values?.file_upload && selectedMember?.file_upload && (
              <div className="mt-2">
                <a
                  href={selectedMember.file_upload}
                  download={selectedMember.file_name}
                  className="text-blue-500 cursor-pointer flex items-center gap-1"
                >
                  <DownloadSimple size={25} />
                  {selectedMember.file_name}
                </a>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="action gap-2">
            <button
              className="secondary-btn w-50"
              type="button"
              onClick={handleClose}
            >
              Cancel
            </button>
            <input
              className="primary-btn w-50"
              type="submit"
              name="submit"
              value={`${selectedMember ? "Edit" : "Add"} Property`}
              disabled={isSubmitting}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default HousingDatabaseModal;
